import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Settings, TrendingUp, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import profiteiaLogo from "@/assets/profiteia-logo.svg";

export function Header() {
  const { getItemCount } = useCart();
  const { user, isAdmin, isDeveloper, signOut } = useAuth();
  const itemCount = getItemCount();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [isDocumentationVisible, setIsDocumentationVisible] = useState(true);

  useEffect(() => {
    const loadDocumentationVisibility = async () => {
      try {
        const result: any = await supabase
          .from("settings" as any)
          .select("value")
          .eq("key", "documentation_visible")
          .single();

        if (!result.error && result.data) {
          const value = result.data.value as { visible: boolean };
          setIsDocumentationVisible(value.visible);
        }
      } catch (error) {
        console.error("Error loading documentation visibility:", error);
      }
    };

    if (user) {
      loadDocumentationVisibility();
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <img src={profiteiaLogo} alt="Profiteia" className="h-9 w-auto" />
        </Link>

        <div className="flex items-center gap-3">
          {!isMobile && (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/">
                  Index
                </Link>
              </Button>

              {user && (
                <>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/catalogus">
                      Catalogus
                    </Link>
                  </Button>


                  {isDocumentationVisible && (
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/api-documentatie">
                        Documentatie
                      </Link>
                    </Button>
                  )}
                </>
              )}
            </>
          )}

          {user && (
            <Button variant="ghost" size="sm" asChild className="relative">
              <Link to="/cart">
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <Badge
                    variant="default"
                    className="absolute -right-1 -top-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                  >
                    {itemCount}
                  </Badge>
                )}
              </Link>
            </Button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="h-4 w-4 mr-2" />
                    Account
                  </Link>
                </DropdownMenuItem>
                {(isAdmin || isDeveloper) && (
                  <DropdownMenuItem asChild>
                    <Link to="/aanvragen" className="cursor-pointer">
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Aanvragen
                    </Link>
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer">
                      <Settings className="h-4 w-4 mr-2" />
                      Beheren
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Uitloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/auth">
                {!isMobile && <User className="h-5 w-5 mr-2" />}
                Inloggen/registreren
              </Link>
            </Button>
          )}

          {isMobile && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-8">
                  <SheetClose asChild>
                    <Button variant="ghost" asChild className="justify-start">
                      <Link to="/">
                        Index
                      </Link>
                    </Button>
                  </SheetClose>
                  {user && (
                    <>
                      <SheetClose asChild>
                        <Button variant="ghost" asChild className="justify-start">
                          <Link to="/catalogus">
                            Catalogus
                          </Link>
                        </Button>
                      </SheetClose>
                      {isDocumentationVisible && (
                        <SheetClose asChild>
                          <Button variant="ghost" asChild className="justify-start">
                            <Link to="/api-documentatie">
                              Documentatie
                            </Link>
                          </Button>
                        </SheetClose>
                      )}
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}
